import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@shared/lib/cn';

/**
 * Label-above text input — the Fancourt auth field style.
 *
 * A small-caps brass label sits above a cream, hairline-bordered input box.
 * Optional `trailing` slot (e.g. a password reveal toggle). Works with
 * react-hook-form `register()` out of the box (uncontrolled, forwardRef).
 */

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  helper?: string;
  error?: string;
  trailing?: ReactNode;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, helper, error, trailing, className, id, ...props },
  ref,
) {
  const reactId = useId();
  const fieldId = id ?? reactId;
  const hasError = !!error;

  return (
    <div className={cn('w-full', className)}>
      <label
        htmlFor={fieldId}
        className="mb-1.5 block text-[0.6875rem] font-semibold uppercase tracking-[0.13em] text-brass"
      >
        {label}
      </label>
      <div
        className={cn(
          'flex items-center gap-2 rounded-md border bg-elevated px-3.5 transition-colors duration-fast',
          'border-hairline-strong focus-within:border-primary-600',
          hasError && 'border-danger focus-within:border-danger',
        )}
      >
        <input
          ref={ref}
          id={fieldId}
          className="h-12 w-full bg-transparent text-body text-ink placeholder:text-ink-muted focus:outline-none"
          aria-invalid={hasError || undefined}
          aria-describedby={helper || error ? `${fieldId}-help` : undefined}
          {...props}
        />
        {trailing ? <div className="flex shrink-0 items-center">{trailing}</div> : null}
      </div>
      {error || helper ? (
        <p
          id={`${fieldId}-help`}
          className={cn('mt-1.5 text-caption', hasError ? 'text-danger' : 'text-ink-muted')}
        >
          {error ?? helper}
        </p>
      ) : null}
    </div>
  );
});
